/**
 * Etapp 2A (Block K) — "Produktdata": katalogkälla, previewimport,
 * snapshots (diff/publish/rollback) och produktfråge-preview.
 * Ingen import eller publicering sker automatiskt när vyn öppnas.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Database, Globe, PlayCircle, RefreshCw, Rocket, RotateCcw,
  Search, ShieldAlert, CheckCircle2, XCircle,
} from "lucide-react";
import { AdminError, AdminLoading } from "./AdminStateViews";
import { CatalogOperations } from "./catalog/CatalogOperations";
import { useCatalogOperations } from "./catalog/useCatalogOperations";
import { statusInfo } from "./catalog/catalogOperationsFormat";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Etapp 2A — defense-in-depth-deduplicering (backend äger invarianten).
const actionIdentity = (a) => {
  const kind = a.render_as || "action";
  if (kind === "product_card" && a.product_id) return `product::${a.product_id}`;
  if (kind === "category_card" && a.category_id) return `category::${a.category_id}`;
  if (kind === "guide" && a.guide_id) return `guide::${a.guide_id}`;
  if (a.action_id) return `${kind}::${a.action_id}`;
  return `${kind}::url::${String(a.url || "").toLowerCase().replace(/\/+$/, "")}`;
};
export const dedupeActions = (list) => {
  const seen = new Set();
  return (list || []).filter((a) => {
    const k = actionIdentity(a);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

function Badge({ tone = "gray", children, ...props }) {
  const tones = {
    gray: "bg-gray-100 text-gray-600",
    green: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700",
  };
  return (
    <span {...props} className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
}

const statusTone = (s) => (
  s === "published" ? "green" : s === "validated" ? "blue"
    : s === "failed" ? "red" : s === "superseded" ? "gray" : "amber");

export const CatalogPanel = ({ clientId, getAuthHeaders }) => {
  const [source, setSource] = useState(null);
  const [allSources, setAllSources] = useState([]);
  const [form, setForm] = useState({ base_url: "", allowed_hosts: "", html_profile: "sitesmart_v5", company_name: "", enabled: true, max_urls: 500, max_crawl_pages: 30 });
  const [snapshots, setSnapshots] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [diff, setDiff] = useState(null);
  const [sample, setSample] = useState(null);
  const [question, setQuestion] = useState("");
  const [chat, setChat] = useState([]);
  const operations = useCatalogOperations(clientId, source?.source_id, getAuthHeaders);
  useEffect(() => {
    if (!operations.data) return;
    setSnapshots(operations.data.snapshots || []);
    setActiveId(operations.data.active?.snapshot_id || null);
  }, [operations.data]);

  const headers = useCallback(() => ({ "Content-Type": "application/json", ...getAuthHeaders() }), [getAuthHeaders]);

  const loadSources = useCallback(async (preferredId) => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/sources`, { headers: headers() });
      if (!res.ok) {
        setLoadError({ status: res.status });
        setSource(null);
        return;
      }
      const data = await res.json();
      const list = data.sources || [];
      setAllSources(list);
      const src = (preferredId && list.find((s) => s.source_id === preferredId)) || list[0] || null;
      setSource(src);
      if (src) {
        setForm({
          base_url: src.base_url,
          allowed_hosts: (src.allowed_hosts || []).join(", "),
          html_profile: src.html_profile || "generic_v1",
          company_name: src.company_name || "",
          enabled: !!src.enabled,
          max_urls: src.crawl_limits?.max_urls ?? 500,
          max_crawl_pages: src.crawl_limits?.max_crawl_pages ?? 30,
        });
        const rs = await fetch(`${API_URL}/api/admin/catalog/${clientId}/sources/${src.source_id}/snapshots`, { headers: headers() });
        if (rs.ok) {
          const sd = await rs.json();
          setSnapshots(sd.snapshots || []);
          setActiveId(sd.active_snapshot_id || null);
        }
      }
    } catch (e) {
      setLoadError({ status: 0, message: String(e.message || e) });
    } finally {
      setLoading(false);
    }
  }, [clientId, headers]);

  useEffect(() => { loadSources(); }, [loadSources]);

  const saveSource = async () => {
    setBusy("save"); setMessage(null);
    try {
      const maxUrls = parseInt(form.max_urls, 10);
      const maxPages = parseInt(form.max_crawl_pages, 10);
      if (!Number.isFinite(maxUrls) || maxUrls < 1 || maxUrls > 20000) {
        throw new Error("Max URL:er måste vara mellan 1 och 20 000.");
      }
      if (!Number.isFinite(maxPages) || maxPages < 1 || maxPages > 5000) {
        throw new Error("Max crawlsidor måste vara mellan 1 och 5000.");
      }
      const body = {
        source_id: source?.source_id || undefined,
        base_url: form.base_url.trim(),
        allowed_hosts: form.allowed_hosts.split(",").map((h) => h.trim()).filter(Boolean),
        html_profile: form.html_profile || undefined,
        company_name: form.company_name || undefined,
        enabled: form.enabled,
        crawl_limits: { ...(source?.crawl_limits || {}), max_urls: maxUrls, max_crawl_pages: maxPages },
      };
      const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/sources`, {
        method: "PUT", headers: headers(), body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || "Kunde inte spara källan");
      setMessage({ tone: "green", text: "Källan sparad." });
      await loadSources(data.source?.source_id);
    } catch (e) { setMessage({ tone: "red", text: String(e.message || e) }); }
    setBusy(null);
  };

  const testHealth = async () => {
    if (!source) return;
    setBusy("health");
    const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/sources/${source.source_id}/health`, { method: "POST", headers: headers() });
    setHealth(res.ok ? await res.json() : { ok: false, error_code: `http_${res.status}` });
    setBusy(null);
  };

  const startImport = async () => {
    if (!source) return;
    setBusy("import"); setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/sources/${source.source_id}/import`, {
        method: "POST", headers: headers(), body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Importen misslyckades");
      setMessage({ tone: "green", text: "Importen har startat. Den aktiva katalogen ändras inte av previewimporten." });
      await operations.refresh();
    } catch (e) { setMessage({ tone: "red", text: String(e.message || e) }); }
    setBusy(null);
  };

  const loadDiff = async (snapshotId) => {
    setBusy(`diff-${snapshotId}`);
    const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/snapshots/${snapshotId}/diff`, { headers: headers() });
    if (res.ok) setDiff({ snapshotId, ...(await res.json()) });
    setBusy(null);
  };

  const loadSample = async (snapshotId) => {
    setBusy(`sample-${snapshotId}`);
    const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/snapshots/${snapshotId}/sample?n=5`, { headers: headers() });
    if (res.ok) setSample({ snapshotId, ...(await res.json()) });
    setBusy(null);
  };

  const publish = async (snapshotId) => {
    setBusy(`publish-${snapshotId}`); setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/snapshots/${snapshotId}/publish`, {
        method: "POST", headers: headers(), body: JSON.stringify({ published_by: "admin" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Publicering misslyckades");
      setMessage({ tone: "green", text: "Snapshot publicerad — aktiv pekare uppdaterad atomiskt." });
      await operations.refresh();
    } catch (e) { setMessage({ tone: "red", text: String(e.message || e) }); }
    setBusy(null);
  };

  const rollback = async () => {
    if (!source) return;
    setBusy("rollback"); setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/sources/${source.source_id}/rollback`, { method: "POST", headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Rollback misslyckades");
      setMessage({ tone: "green", text: "Rollback klar — föregående snapshot är aktiv igen." });
      await operations.refresh();
    } catch (e) { setMessage({ tone: "red", text: String(e.message || e) }); }
    setBusy(null);
  };

  const cleanupSnapshots = async () => {
    if (!source) return;
    setBusy("cleanup"); setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/sources/${source.source_id}/cleanup-snapshots`, { method: "POST", headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Rensning misslyckades");
      const nd = (data.deleted || []).length; const nf = (data.marked_failed || []).length;
      setMessage({ tone: "green", text: `Rensning klar — ${nd} tomma/misslyckade snapshots borttagna, ${nf} hängande markerade som misslyckade.` });
      await operations.refresh();
    } catch (e) { setMessage({ tone: "red", text: String(e.message || e) }); }
    setBusy(null);
  };

  const [focusData, setFocusData] = useState(null);
  const [focusQ, setFocusQ] = useState("");
  const [focusFilter, setFocusFilter] = useState("all");
  const focusReadyRef = useRef(false);
  const loadFocus = async (q = focusQ, filter = focusFilter) => {
    setBusy("focus");
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/active-products?q=${encodeURIComponent(q)}&filter=${filter}`, { headers: headers() });
      const data = await res.json();
      if (res.ok) { setFocusData(data); focusReadyRef.current = true; }
    } catch (e) { setMessage({ tone: "red", text: String(e.message || e) }); }
    setBusy(null);
  };
  // Debounced sök: när panelen väl laddats filtreras listan medan man skriver
  // (Enter behövs inte). Enter/blur ger fortsatt omedelbar sökning.
  useEffect(() => {
    if (!focusReadyRef.current) return undefined;
    const t = setTimeout(() => { loadFocus(focusQ, focusFilter); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusQ]);
  const toggleFocus = async (p) => {
    const next = p.priority === "preferred" ? "normal" : "preferred";
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/focus`, { method: "POST", headers: headers(), body: JSON.stringify({ product_key: p.product_key, priority: next, sku: p.sku, ean: p.ean, product_name: p.product_name }) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || d.error || "Kunde inte spara"); }
      await loadFocus();
    } catch (e) { setMessage({ tone: "red", text: String(e.message || e) }); }
  };

  const deleteSnapshot = async (snapshotId) => {
    setBusy(`delete-${snapshotId}`); setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/snapshots/${snapshotId}`, { method: "DELETE", headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Kunde inte ta bort snapshoten");
      setMessage({ tone: "green", text: "Snapshot borttagen." });
      await operations.refresh();
    } catch (e) { setMessage({ tone: "red", text: String(e.message || e) }); }
    setBusy(null);
  };

  const ask = async () => {
    if (!source || !question.trim()) return;
    const msg = question.trim();
    setBusy("ask");
    try {
      const history = chat.map(({ role, content }) => ({ role, content }));
      const res = await fetch(`${API_URL}/api/admin/catalog/${clientId}/test-question`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({ source_id: source.source_id, message: msg, history }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Testfrågan misslyckades");
      setChat((prev) => [...prev,
        { role: "user", content: msg },
        { role: "assistant", content: data.reply || "", meta: data }]);
      setQuestion("");
    } catch (e) {
      setChat((prev) => [...prev,
        { role: "user", content: msg },
        { role: "assistant", content: "", meta: { error: String(e.message || e) } }]);
    }
    setBusy(null);
  };

  const resetChat = () => { setChat([]); setQuestion(""); };

  // Admin Cleanup 0 — Produktdata är frikopplad från blueprint och får
  // aldrig fastna i spinner eller bli tom: laddning → felvy → innehåll.
  if (loading && !source && !loadError) {
    return <AdminLoading label="Laddar produktdata…" />;
  }
  if (loadError) {
    return (
      <AdminError
        what={`Produktdata för ${clientId}`}
        status={loadError.status}
        message={loadError.message}
        onRetry={loadSources}
      />
    );
  }

  return (
    <div className="space-y-5" data-testid="catalog-panel">
      {source && <CatalogOperations {...operations} onRefresh={operations.refresh} />}
      {!source && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-600" data-testid="catalog-empty-state">
          <p className="font-semibold text-gray-800">Ingen produktkälla är konfigurerad ännu.</p>
          <p className="mt-1">Produktdata fungerar oberoende av kundens AI-agent — även för kunder utan agentunderlag eller som kör äldre uppsättning. Fyll i en publik katalogkälla nedan för att komma igång.</p>
        </div>
      )}
      {message && (
        <div className={`rounded-md px-3 py-2 text-xs font-medium ${message.tone === "green" ? "bg-emerald-50 text-emerald-700" : message.tone === "amber" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`} data-testid="catalog-message">
          {message.text}
        </div>
      )}

      {/* Källkonfiguration */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Globe className="w-4 h-4" /> Publik katalogkälla</h3>
        {allSources.length > 1 && (
          <label className="text-xs text-gray-600 block">Datakälla
            <select
              data-testid="catalog-source-select"
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs"
              value={source?.source_id || ""}
              onChange={(e) => loadSources(e.target.value)}
            >
              {allSources.map((s) => (
                <option key={s.source_id} value={s.source_id}>{s.base_url || s.source_id}</option>
              ))}
            </select>
          </label>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-xs text-gray-600">Base URL (https)
            <input data-testid="catalog-source-base-url" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="https://www.example.se/" />
          </label>
          <label className="text-xs text-gray-600">Allowlistade hosts (kommaseparerade)
            <input data-testid="catalog-source-hosts" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs" value={form.allowed_hosts} onChange={(e) => setForm({ ...form, allowed_hosts: e.target.value })} placeholder="www.example.se" />
          </label>
          <label className="text-xs text-gray-600">Företagsnamn (pilotpreview)
            <input data-testid="catalog-source-company" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
          </label>
        </div>
        <details className="rounded border border-gray-200 bg-gray-50" data-testid="catalog-advanced-fields">
          <summary className="px-3 py-2 cursor-pointer text-xs font-semibold text-gray-700 select-none">Avancerat (tekniska katalogfält)</summary>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
            <label className="text-xs text-gray-600">HTML-profil
              <select data-testid="catalog-source-profile" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs" value={form.html_profile} onChange={(e) => setForm({ ...form, html_profile: e.target.value })}>
                <option value="generic_v1">generic_v1</option>
                <option value="sitesmart_v5">sitesmart_v5</option>
              </select>
            </label>
            <label className="text-xs text-gray-600">Max URL:er per import (1–20 000)
              <input data-testid="catalog-source-max-urls" type="number" min="1" max="20000" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs" value={form.max_urls} onChange={(e) => setForm({ ...form, max_urls: e.target.value })} />
              <span className="mt-0.5 block text-[10px] text-gray-500">Övre gräns för hur många upptäckta URL:er som hämtas per import. Höj om discovery-taket nås — högre värde ger fler produkter men längre importtid.</span>
            </label>
            <label className="text-xs text-gray-600">Max crawlsidor (1–5000)
              <input data-testid="catalog-source-max-crawl-pages" type="number" min="1" max="5000" className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs" value={form.max_crawl_pages} onChange={(e) => setForm({ ...form, max_crawl_pages: e.target.value })} />
              <span className="mt-0.5 block text-[10px] text-gray-500">Max antal sidor crawlern besöker för att hitta produktlänkar (används när sitemap saknas). Höj om delar av sortimentet inte upptäcks.</span>
            </label>
          </div>
        </details>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="catalog-save-source-btn" onClick={saveSource} disabled={busy === "save"} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
            <Database className="w-3.5 h-3.5" /> Spara källa
          </button>
          <button data-testid="catalog-health-btn" onClick={testHealth} disabled={!source || busy === "health"} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className="w-3.5 h-3.5" /> Testa source health
          </button>
          <button data-testid="catalog-import-btn" onClick={startImport} disabled={!source || busy === "import" || operations.loading || operations.data?.import_in_progress} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 text-emerald-700 px-3 py-1.5 text-xs font-semibold hover:bg-emerald-50 disabled:opacity-50">
            <PlayCircle className="w-3.5 h-3.5" /> Starta previewimport
          </button>
          {health && (
            <span data-testid="catalog-health-result" className="inline-flex items-center gap-1 text-xs">
              {health.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
              {health.ok ? "Källan svarar" : `Fel: ${health.error_code || "okänt"}`}
            </span>
          )}
        </div>
      </div>

      {/* Snapshots */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Rocket className="w-4 h-4" /> Snapshots</h3>
        {snapshots.length === 0 && <p className="text-xs text-gray-500">Inga snapshots ännu — starta en previewimport.</p>}
        <div className="space-y-2">
          {snapshots.map((s) => {
            const vs = s.validation_summary || {};
            const productCount = vs.product_count ?? null;
            const otherPages = productCount != null ? Math.max(0, (s.accepted_entity_count || 0) - productCount) : null;
            const cov = vs.discovery_coverage || {};
            const canPublish = s.status === "validated" && (productCount == null || productCount > 0);
            const removable = s.status === "failed" || (s.status === "validated" && s.snapshot_id !== activeId && (productCount === 0));
            return (
            <div key={s.snapshot_id} className="rounded border border-gray-100 p-2.5 text-xs flex flex-wrap items-center gap-2" data-testid={`catalog-snapshot-${s.snapshot_id}`}>
              <Badge tone={statusTone(s.status)} data-testid={`catalog-snapshot-status-${s.snapshot_id}`}>{statusInfo(s.status)[0]}</Badge>
              {s.snapshot_id === activeId && <Badge tone="green" data-testid={`catalog-snapshot-active-${s.snapshot_id}`}>AKTIV</Badge>}
              <span className="font-mono text-[10px] text-gray-500" data-testid={`catalog-snapshot-id-${s.snapshot_id}`}>{s.snapshot_id}</span>
              <span className="text-gray-600" data-testid={`catalog-snapshot-counts-${s.snapshot_id}`}>
                upptäckta sidor: {s.discovered_url_count}
                {productCount != null && <> · <span className="font-semibold text-emerald-700">parsade produkter: {productCount}</span></>}
                {otherPages != null && <> · övriga sidor: {otherPages}</>}
                {" "}· avvisade: {s.rejected_entity_count}
              </span>
              {s.status === "validated" && productCount === 0 && (
                <span data-testid={`catalog-no-products-warning-${s.snapshot_id}`} className="text-red-700 font-semibold">
                  ⚠ Inga produkter kunde tolkas — katalogen kan inte publiceras. Kontrollera HTML-profil/källa och importera igen.
                </span>
              )}
              {vs.rejected_reason_counts && Object.keys(vs.rejected_reason_counts).length > 0 && (
                <span data-testid={`catalog-reject-counts-${s.snapshot_id}`} className="text-gray-500">
                  avvisningar per orsak: {Object.entries(vs.rejected_reason_counts).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                </span>
              )}
              {cov.discovery_capped && (
                <span data-testid={`catalog-cap-warning-${s.snapshot_id}`} className="text-amber-700 font-medium">
                  {(cov.urls_skipped_over_cap || 0) > 0
                    ? `⚠ Importen tog ${s.discovered_url_count} av ${(s.discovered_url_count || 0) + (cov.urls_skipped_over_cap || 0)} upptäckta sidor (taket "Max URL:er per import" nåddes). Höj värdet för full täckning.`
                    : cov.truncated_reason === "max_crawl_pages"
                      ? "⚠ Crawlbudgeten (Max crawlsidor/djup) tog slut innan alla sidor hittades — sajten saknar sitemap. Höj Max crawlsidor eller lägg till en sitemap."
                      : "⚠ Fler sidor kan finnas på sajten, men återstående antal är okänt — höj Max URL:er/Max crawlsidor och importera igen för full täckning."}
                </span>
              )}
              <span className="flex-1" />
              <button onClick={() => loadDiff(s.snapshot_id)} className="underline text-blue-700" data-testid={`catalog-diff-btn-${s.snapshot_id}`}>Diff</button>
              <button onClick={() => loadSample(s.snapshot_id)} className="underline text-blue-700" data-testid={`catalog-sample-btn-${s.snapshot_id}`}>Granska 5</button>
              {canPublish && (
                <button onClick={() => publish(s.snapshot_id)} className="rounded bg-emerald-600 text-white px-2 py-1 font-semibold" data-testid={`catalog-publish-btn-${s.snapshot_id}`}>Publicera</button>
              )}
              {removable && (
                <button onClick={() => deleteSnapshot(s.snapshot_id)} disabled={busy === `delete-${s.snapshot_id}`} className="underline text-red-600 disabled:opacity-40" data-testid={`catalog-delete-btn-${s.snapshot_id}`}>Ta bort</button>
              )}
            </div>
          );})}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="catalog-rollback-btn" onClick={rollback} disabled={busy === "rollback"} className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 text-amber-700 px-3 py-1.5 text-xs font-semibold hover:bg-amber-50 disabled:opacity-50">
            <RotateCcw className="w-3.5 h-3.5" /> Rollback till föregående snapshot
          </button>
          <button data-testid="catalog-cleanup-btn" onClick={cleanupSnapshots} disabled={busy === "cleanup"} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 text-gray-700 px-3 py-1.5 text-xs font-semibold hover:bg-gray-50 disabled:opacity-50">
            <XCircle className="w-3.5 h-3.5" /> Rensa tomma/misslyckade snapshots
          </button>
        </div>

        <div className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid="salesfocus-panel">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900">Säljfokus (prioriterade produkter)</p>
            <button onClick={() => loadFocus()} disabled={busy === "focus"} className="text-xs underline text-blue-700" data-testid="salesfocus-load">Läs in produkter ur aktiv katalog</button>
          </div>
          {focusData && (
            <>
              <p className="text-xs text-gray-500" data-testid="salesfocus-count">{focusData.product_total} produkter · {focusData.preferred_count} prioriterade{focusData.active_snapshot_id ? ` · aktiv snapshot: ${focusData.active_snapshot_id}` : ""}</p>
              <div className="flex gap-2 flex-wrap">
                <input value={focusQ} onChange={(e) => setFocusQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadFocus(focusQ, focusFilter)} placeholder="Sök produkt / SKU / EAN" className="flex-1 min-w-40 rounded border border-gray-300 px-2 py-1 text-xs" data-testid="salesfocus-search" />
                <select value={focusFilter} onChange={(e) => { setFocusFilter(e.target.value); loadFocus(focusQ, e.target.value); }} className="rounded border border-gray-300 px-2 py-1 text-xs" data-testid="salesfocus-filter">
                  <option value="all">Alla</option>
                  <option value="preferred">Prioriterade</option>
                  <option value="normal">Normala</option>
                  <option value="missing">Saknas i senaste katalogen</option>
                </select>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
                {(focusData.products || []).map((p) => (
                  <div key={p.product_key} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`salesfocus-row-${p.product_key}`}>
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{p.product_name} {!p.in_catalog && <Badge tone="red">saknas i katalog</Badge>}</p>
                      <p className="text-gray-400 truncate">{(p.category_path || []).join(" / ")}{p.preview_price != null ? ` · ${p.preview_price} ${p.preview_currency || "kr"}` : ""}{p.focus_source ? ` · källa: ${p.focus_source}` : ""}</p>
                    </div>
                    <button onClick={() => toggleFocus(p)} className={`rounded px-2 py-1 font-semibold ${p.priority === "preferred" ? "bg-amber-500 text-white" : "border border-gray-300 text-gray-700"}`} data-testid={`salesfocus-toggle-${p.product_key}`}>
                      {p.priority === "preferred" ? "★ Prioriterad" : "Gör prioriterad"}
                    </button>
                  </div>
                ))}
                {(focusData.products || []).length === 0 && <p className="text-gray-400 py-2">Inga produkter för valt filter.</p>}
              </div>
            </>
          )}
        </div>

        {diff && (
          <div className="rounded bg-gray-50 p-3 text-xs" data-testid="catalog-diff-result">
            <p className="font-semibold mb-1">Diff mot aktiv snapshot ({diff.against_active_snapshot_id || "ingen aktiv"})</p>
            <p>+{diff.diff.added_count} tillagda · −{diff.diff.removed_count} borttagna · {diff.diff.changed_count} ändrade · {diff.diff.unchanged_count} oförändrade</p>
          </div>
        )}
        {sample && (
          <div className="rounded bg-gray-50 p-3 text-xs space-y-2" data-testid="catalog-sample-result">
            <p className="font-semibold">Produktgranskning — {sample.products.length} av {sample.product_total ?? sample.products.length} produkter</p>
            {sample.products.length === 0 && (
              <p className="text-red-600" data-testid="catalog-sample-empty">Inga produkter i den här snapshoten (endast kategorisidor/URL:er tolkades). Kontrollera källa/HTML-profil och importera igen.</p>
            )}
            {sample.products.map((p) => (
              <div key={p.product_id + (p.canonical_url || "")} className="border-b border-gray-200 pb-1.5" data-testid="catalog-sample-product">
                <p className="font-medium">{p.product_name} {p.discontinued && <Badge tone="red">utgången</Badge>} {p.restricted_classification !== "standard" && <Badge tone="amber"><ShieldAlert className="w-3 h-3 inline" /> {p.restricted_classification}</Badge>}</p>
                <p className="text-emerald-700 font-medium" data-testid="catalog-sample-price">
                  {p.preview_price != null ? `${new Intl.NumberFormat("sv-SE").format(p.preview_price)} ${p.preview_currency || "kr"}` : "Pris ej tolkat"}
                  {p.preview_availability && <span className="text-gray-400 font-normal"> · {p.preview_availability}</span>}
                </p>
                <p className="text-gray-500">Kategori: {(p.category_path || []).join(" / ") || "—"}{p.sku ? ` · SKU: ${p.sku}` : ""}</p>
                {p.source_url && <a href={p.source_url} target="_blank" rel="noreferrer" className="text-blue-700 underline truncate block" data-testid="catalog-sample-source">{p.source_url}</a>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Testfråga — flerturnschatt i sandbox */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2"><Search className="w-4 h-4" /> Testa produktfrågor (sandbox — noll leads, noll mejl)</h3>
          <button data-testid="catalog-chat-reset-btn" onClick={resetChat} disabled={chat.length === 0} className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-[11px] font-semibold hover:bg-gray-50 disabled:opacity-40">
            <RotateCcw className="w-3 h-3" /> Nollställ samtal
          </button>
        </div>
        <p className="text-[11px] text-gray-400">Historiken skickas med varje tur — testa flerturnsminne, t.ex. "Vad kostar TVM60A?" → "Finns den i lager?", eller budget som hålls över flera turer.</p>

        {chat.length > 0 && (
          <div className="space-y-2 max-h-96 overflow-y-auto rounded bg-gray-50 p-3" data-testid="catalog-chat-log">
            {chat.map((m, i) => (
              <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                <div className={`inline-block max-w-[85%] rounded-lg px-3 py-2 text-xs whitespace-pre-wrap text-left ${m.role === "user" ? "bg-gray-900 text-white" : "bg-white border border-gray-200"}`}
                  data-testid={m.role === "user" ? "catalog-chat-user-msg" : "catalog-chat-assistant-msg"}>
                  {m.meta?.error ? <span className="text-red-600">{m.meta.error}</span> : m.content}
                </div>
                {m.role === "assistant" && !m.meta?.error && (
                  <div className="mt-1 space-y-1.5">
                    <div className="flex flex-wrap gap-1.5">
                      <Badge tone="blue">match: {m.meta?.catalog_match_type || "—"}</Badge>
                      <Badge tone={m.meta?.llm_called ? "amber" : "green"}>{m.meta?.llm_called ? "LLM användes" : "0 LLM-anrop"}</Badge>
                      {m.meta?.offer_lookup_status && <Badge tone={m.meta.offer_lookup_status === "valid" ? "green" : "amber"}>offer: {m.meta.offer_lookup_status}</Badge>}
                      <Badge tone="gray">{m.meta?.answer_label || m.meta?.answer_class}</Badge>
                      {m.meta?.tested_snapshot_id && (
                        <Badge tone="blue" data-testid="catalog-tested-snapshot">testad snapshot: {m.meta.tested_snapshot_id} ({m.meta.tested_snapshot_status || "?"})</Badge>
                      )}
                    </div>
                    {(m.meta?.actions || []).length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {dedupeActions(m.meta.actions || []).map((a) => (
                          a.render_as === "product_card" ? (
                            <a key={a.action_id} href={a.url} target="_blank" rel="noreferrer" data-testid="catalog-preview-product-card" className="block rounded-md border border-gray-200 bg-white p-2.5 hover:border-emerald-400 transition-colors">
                              <p className="text-xs font-semibold text-gray-900">{a.label}</p>
                              {(a.price_label || a.availability_label) && (
                                <p className="text-xs text-emerald-700 font-medium">
                                  {a.price_label}{a.price_label && a.availability_label ? " · " : ""}{a.availability_label}
                                  {a.freshness_label && <span className="text-gray-400 font-normal"> ({a.freshness_label})</span>}
                                </p>
                              )}
                              <p className="text-[10px] text-gray-400 truncate">{a.source_label}{a.category ? ` · ${a.category}` : ""}</p>
                            </a>
                          ) : (
                            <a key={a.action_id} href={a.url} target="_blank" rel="noreferrer" data-testid="catalog-preview-category-card" className="block rounded-md border border-dashed border-gray-300 bg-white p-2.5 hover:border-blue-400 transition-colors">
                              <p className="text-xs font-semibold text-blue-800">{a.label}</p>
                              <p className="text-[10px] text-gray-400">[{a.render_as}] {a.description || ""}</p>
                            </a>
                          )
                        ))}
                      </div>
                    )}
                    <details>
                      <summary className="cursor-pointer text-[10px] text-gray-400">Answer Receipt</summary>
                      <pre className="text-[10px] overflow-auto max-h-64">{JSON.stringify(m.meta?.answer_receipt, null, 2)}</pre>
                    </details>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <input data-testid="catalog-question-input" className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-xs" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="T.ex. Vi är fem i familjen och behöver en ny tvättmaskin…" onKeyDown={(e) => e.key === "Enter" && ask()} />
          <button data-testid="catalog-ask-btn" onClick={ask} disabled={busy === "ask"} className="rounded-md bg-gray-900 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50">{busy === "ask" ? "Frågar…" : "Fråga"}</button>
        </div>
      </div>
    </div>
  );
};

export default CatalogPanel;
